/**
 * The landing page: this month's headline figures, what needs doing today,
 * profit over the last year, where things sell, how the stock moves through
 * its stages, the latest sales and the stock that has waited longest.
 * Every panel is gated by the permission its data needs.
 */
import { Link } from "react-router";
import type { FinanceSummary, ItemSummary, SaleRow } from "@sable/api-contract";
import { IconAlert, IconBox, IconCheck, IconTag } from "../components/icons.tsx";
import { Kpis } from "../components/Kpis.tsx";
import { MarketplaceLogo } from "../components/MarketplaceLogo.tsx";
import { MonthBars } from "../components/MonthBars.tsx";
import { ErrorBlock, Loading, MarketplaceMark } from "../components/ui.tsx";
import { currentLocale, t, tk } from "../i18n/index.ts";
import { money, percent, shortDate, todayIn } from "../lib/format.ts";
import { useFinance, useItems, useMarketplaces, useSales, useUnmatchedSales } from "../lib/queries.ts";
import { useCan, useWs, wsPath } from "../lib/workspace.tsx";

const PIPELINE = ["draft", "in_stock", "listed", "sold", "shipped"] as const;
const DAY = 86_400_000;

/** The same window as Finance's "Last 12 months", so the two share a cached query. */
function last12(today: string): { from: string; to: string } {
  const [y, m] = today.split("-").map(Number) as [number, number];
  return { from: new Date(Date.UTC(y, m - 12, 1)).toISOString().slice(0, 10), to: today };
}

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY));
}

/** A horizontal bar for a share of the largest value; SVG attributes, since the CSP allows no inline styles. */
function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.max(value > 0 ? 2 : 0, Math.round((value / max) * 100)) : 0;
  return (
    <svg className="meter" width="100%" height="8" role="img" aria-label={label}>
      <rect className="meter-track" x="0" y="0" width="100%" height="8" rx="4" />
      {pct > 0 && <rect className="meter-fill" x="0" y="0" width={`${pct}%`} height="8" rx="4" />}
    </svg>
  );
}

interface Task {
  key: string;
  count: number;
  label: string;
  to: string;
  tone: "warn" | "info";
}

/** What needs a person: sales to ship, sales to tie to an item, stock to finish or list. */
function Attention({ counts }: { counts: Record<string, number> | undefined }) {
  const ws = useWs();
  const canSales = useCan("sales.write");
  const pending = useSales(ws.id, "pending", canSales);
  const unmatched = useUnmatchedSales(ws.id, canSales);
  const tasks: Task[] = [];
  if (canSales && pending.data?.length) {
    tasks.push({ key: "ship", count: pending.data.length, label: t("dashboard.todo.ship", { count: pending.data.length }), to: "/sales", tone: "warn" });
  }
  if (canSales && unmatched.data?.length) {
    tasks.push({ key: "unmatched", count: unmatched.data.length, label: t("dashboard.todo.unmatched", { count: unmatched.data.length }), to: "/sales", tone: "warn" });
  }
  const drafts = counts?.draft ?? 0;
  if (drafts) tasks.push({ key: "draft", count: drafts, label: t("dashboard.todo.drafts", { count: drafts }), to: "/inventory", tone: "info" });
  const unlisted = counts?.in_stock ?? 0;
  if (unlisted) tasks.push({ key: "list", count: unlisted, label: t("dashboard.todo.unlisted", { count: unlisted }), to: "/inventory?status=in_stock", tone: "info" });

  const loading = !counts || (canSales && (pending.isPending || unmatched.isPending));
  return (
    <section className="card dash-card">
      <h2>{t("dashboard.todo.title")}</h2>
      {loading ? <Loading /> : tasks.length === 0 ? (
        <p className="dash-clear"><IconCheck size={18} />{t("dashboard.todo.clear")}</p>
      ) : (
        <div className="list">
          {tasks.map((task) => (
            <Link key={task.key} className="list-row dash-task" to={wsPath(ws, task.to)}>
              <span className={`dash-task-icon ${task.tone}`}>{task.tone === "warn" ? <IconAlert size={18} /> : <IconTag size={18} />}</span>
              <span className="grow">{task.label}</span>
              <b className="num">{task.count}</b>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function ProfitTrend({ data }: { data: FinanceSummary | undefined }) {
  return (
    <section className="card dash-card">
      <h2>{t("finance.net")} · {t("finance.last12")}</h2>
      {!data ? <Loading /> : data.months.length === 0 ? <p className="muted small">{t("finance.noSales")}</p> : (
        <>
          <MonthBars data={data} />
          <div className="dash-foot">
            <span>{t("finance.revenue")} <b className="num">{money(data.grossRevenueMinor, data.currency)}</b></span>
            <span>{t("finance.net")} <b className={`num${data.netProfitMinor < 0 ? " neg" : ""}`}>{money(data.netProfitMinor, data.currency)}</b></span>
            {data.grossRevenueMinor > 0 && <span>{t("finance.margin")} <b className="num">{percent(data.netProfitMinor / data.grossRevenueMinor)}</b></span>}
          </div>
        </>
      )}
    </section>
  );
}

/** Revenue by marketplace over the year: the mark and name carry identity, so the bars share one colour. */
function Channels({ data }: { data: FinanceSummary | undefined }) {
  const mps = useMarketplaces();
  const rows = [...(data?.byMarketplace ?? [])].sort((a, b) => b.grossRevenueMinor - a.grossRevenueMinor);
  const max = Math.max(0, ...rows.map((r) => r.grossRevenueMinor));
  const total = rows.reduce((a, r) => a + r.grossRevenueMinor, 0);
  return (
    <section className="card dash-card">
      <h2>{t("finance.byMarketplace")} · {t("finance.last12")}</h2>
      {!data ? <Loading /> : rows.length === 0 ? <p className="muted small">{t("finance.noSales")}</p> : (
        <div className="dash-bars">
          {rows.map((r) => {
            const name = mps.data?.find((m) => m.code === r.marketplace)?.name ?? r.marketplace;
            const share = total > 0 ? percent(r.grossRevenueMinor / total) : "";
            return (
              <div key={r.marketplace} className="dash-bar-row" title={t("dashboard.channel.tip", { name, units: r.unitsSold, profit: money(r.netProfitMinor, data.currency) })}>
                <MarketplaceLogo code={r.marketplace} size={24} />
                <div className="grow stack-sm">
                  <div className="spread small">
                    <span className="dash-bar-name">{name}</span>
                    <span className="num">{money(r.grossRevenueMinor, data.currency)} <span className="muted">· {share}</span></span>
                  </div>
                  <Meter value={r.grossRevenueMinor} max={max} label={`${name}: ${money(r.grossRevenueMinor, data.currency)}`} />
                  <span className="muted small">{t("dashboard.channel.units", { count: r.unitsSold })}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** How many items sit at each stage, and the year's sell-through when finance is readable. */
function Pipeline({ counts, year }: { counts: Record<string, number> | undefined; year: FinanceSummary | undefined }) {
  const ws = useWs();
  const max = Math.max(0, ...PIPELINE.map((s) => counts?.[s] ?? 0));
  const sellThrough = year && year.unitsSold + year.inventory.activeUnits > 0
    ? year.unitsSold / (year.unitsSold + year.inventory.activeUnits)
    : null;
  return (
    <section className="card dash-card">
      <h2>{t("dashboard.pipeline.title")}</h2>
      {!counts ? <Loading /> : (
        <>
          <div className="dash-bars">
            {PIPELINE.map((s) => {
              const n = counts[s] ?? 0;
              return (
                <Link key={s} className="dash-stage" to={wsPath(ws, s === "draft" ? "/inventory" : `/inventory?status=${s}`)}>
                  <span className="dash-stage-name small">{tk("status", s)}</span>
                  <Meter value={n} max={max} label={`${tk("status", s)}: ${n}`} />
                  <b className="num">{n}</b>
                </Link>
              );
            })}
          </div>
          {sellThrough !== null && (
            <div className="dash-foot">
              <span>{t("dashboard.pipeline.sellThrough")} <b className="num">{percent(sellThrough)}</b></span>
              <span className="muted">{t("dashboard.pipeline.sellThroughHint")}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SaleLine({ sale }: { sale: SaleRow }) {
  const ws = useWs();
  const canFinance = useCan("finance.read");
  return (
    <Link className="list-row dash-line" to={wsPath(ws, `/items/${sale.itemId}`)}>
      <span className="dash-thumb">{sale.thumbUrl ? <img src={sale.thumbUrl} alt="" loading="lazy" /> : <IconBox size={18} />}</span>
      <span className="grow">
        <span className="name">{sale.title || t("inventory.untitled")}</span>
        <span className="sub row"><MarketplaceMark code={sale.marketplace} />{shortDate(sale.soldAt)}{!sale.shippedAt && <> · {t("dashboard.sales.toShip")}</>}</span>
      </span>
      <span className="dash-amount">
        <b className="num">{money(sale.salePriceMinor, sale.currency)}</b>
        {canFinance && sale.netProfitMinor !== null && (
          <span className={`small num ${sale.netProfitMinor >= 0 ? "pos" : "neg"}`}>{t("sales.profit", { amount: money(sale.netProfitMinor, sale.currency) })}</span>
        )}
      </span>
    </Link>
  );
}

function RecentSales() {
  const ws = useWs();
  const sales = useSales(ws.id, "all");
  const recent = (sales.data ?? []).slice(0, 5);
  return (
    <section className="card dash-card">
      <div className="spread dash-head">
        <h2>{t("sales.recent")}</h2>
        <Link className="small" to={wsPath(ws, "/sales")}>{t("dashboard.seeAll")}</Link>
      </div>
      {sales.isPending ? <Loading /> : sales.isError ? <ErrorBlock error={sales.error} onRetry={() => void sales.refetch()} /> :
        recent.length === 0 ? <p className="muted small">{t("sales.empty")}</p> : (
          <div className="list">{recent.map((s) => <SaleLine key={s.saleId} sale={s} />)}</div>
        )}
    </section>
  );
}

function AgingLine({ item }: { item: ItemSummary }) {
  const ws = useWs();
  const days = daysSince(item.createdAt);
  return (
    <Link className="list-row dash-line" to={wsPath(ws, `/items/${item.id}`)}>
      <span className="dash-thumb">{item.thumbUrl ? <img src={item.thumbUrl} alt="" loading="lazy" /> : <IconBox size={18} />}</span>
      <span className="grow">
        <span className="name">{item.title || t("inventory.untitled")}</span>
        <span className="sub row">
          {item.marketplaces.map((c) => <MarketplaceMark key={c} code={c} />)}
          {t("dashboard.aging.days", { count: days })}
        </span>
      </span>
      <span className="dash-amount">
        <b className="num">{item.listPriceMinor !== null ? money(item.listPriceMinor, item.currency) : "—"}</b>
      </span>
    </Link>
  );
}

/** Listed stock that has waited longest since intake: candidates for a price drop or a fresh listing. */
function Aging({ items }: { items: ItemSummary[] | undefined }) {
  const ws = useWs();
  return (
    <section className="card dash-card">
      <div className="spread dash-head">
        <h2>{t("dashboard.aging.title")}</h2>
        <Link className="small" to={wsPath(ws, "/inventory?status=listed&sort=oldest")}>{t("dashboard.seeAll")}</Link>
      </div>
      {!items ? <Loading /> : items.length === 0 ? <p className="muted small">{t("dashboard.aging.empty")}</p> : (
        <div className="list">{items.slice(0, 5).map((i) => <AgingLine key={i.id} item={i} />)}</div>
      )}
    </section>
  );
}

export function Dashboard() {
  const ws = useWs();
  const canFinance = useCan("finance.read");
  const canSales = useCan("sales.write");
  const today = todayIn(ws.timezone);
  const { from, to } = last12(today);
  const year = useFinance(ws.id, from, to, false, canFinance);
  const listed = useItems(ws.id, { status: "listed", sort: "oldest" });
  const counts = listed.data?.pages[0]?.counts;
  const yearData = canFinance ? year.data : undefined;
  const dateLine = new Date(`${today}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: "long", month: "long", day: "numeric" });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t("dashboard.title")}</h1>
          <p className="sub">{dateLine}</p>
        </div>
        <div className="row-wrap">
          <Link className="btn btn-ghost" to={wsPath(ws, "/inventory")}>{t("nav.inventory")}</Link>
          <Link className="btn btn-primary" to={wsPath(ws, "/items/new")}>{t("nav.add")}</Link>
        </div>
      </div>

      {canFinance && <Kpis stockValue />}

      {listed.isError && <ErrorBlock error={listed.error} onRetry={() => void listed.refetch()} />}

      <div className="dash-grid">
        <Attention counts={counts} />
        <Pipeline counts={counts} year={yearData} />
        {canFinance && <ProfitTrend data={yearData} />}
        {canFinance && <Channels data={yearData} />}
        {canSales && <RecentSales />}
        <Aging items={listed.data?.pages[0]?.items} />
      </div>
    </>
  );
}
